import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/HomePage.css';

interface Example {
  id: string;
  title: string;
  description15: string;
  description10: string;
  complexity: 'Basic' | 'Intermediate' | 'Advanced' | 'Expert';
  route: string;
  prompt: string;
  features15: string[];
  features10: string[];
  timeTaken15: string;
  timeTaken10: string;
  developerTime15: string;
  developerTime10: string;
}

const examples: Example[] = [
  {
    id: 'memory-game',
    title: 'Memory Game',
    description15: 'Production-ready memory game with WebGL animations, AI difficulty adjustment, multiplayer support, and comprehensive analytics',
    description10: 'Basic memory card matching game with simple click interactions',
    complexity: 'Basic',
    route: '/memory-game',
    prompt: 'Create a memory card game with animals',
    features15: ['WebGL 3D animations', 'AI difficulty scaling', 'Real-time multiplayer', 'Performance analytics', 'Accessibility compliance', 'PWA support'],
    features10: ['Basic card flip', 'Simple matching', 'Static layout'],
    timeTaken15: '8 minutes',
    timeTaken10: '3.5 minutes',
    developerTime15: '4-6 weeks',
    developerTime10: '3-5 days'
  },
  {
    id: 'todo-app',
    title: 'Task Manager',
    description15: 'Enterprise-grade task management with real-time collaboration, advanced filtering, drag & drop, offline sync, and team workspaces',
    description10: 'Simple todo list with add/remove functionality',
    complexity: 'Basic',
    route: '/todo-app',
    prompt: 'Build a todo list application',
    features15: ['Real-time collaboration', 'Advanced filtering & search', 'Drag & drop reordering', 'Offline sync', 'Team workspaces', 'Due date reminders'],
    features10: ['Add/delete tasks', 'Mark complete', 'Local storage'],
    timeTaken15: '6 minutes',
    timeTaken10: '2.8 minutes',
    developerTime15: '8-12 weeks',
    developerTime10: '2-3 days'
  },
  {
    id: 'weather-dashboard',
    title: 'Weather Platform',
    description15: 'Professional weather platform with satellite imagery, detailed forecasts, weather alerts, historical data, and interactive maps',
    description10: 'Basic weather display showing current temperature',
    complexity: 'Intermediate',
    route: '/weather-dashboard',
    prompt: 'Create a weather dashboard application',
    features15: ['Live satellite imagery', 'Interactive weather maps', 'Severe weather alerts', 'Historical data analysis', 'Location-based services', 'Weather widgets'],
    features10: ['Current temperature', 'Basic forecast', 'Location search'],
    timeTaken15: '9.5 minutes',
    timeTaken10: '4.2 minutes',
    developerTime15: '12-16 weeks',
    developerTime10: '1-2 weeks'
  },
  {
    id: 'ecommerce-dashboard',
    title: 'E-commerce Analytics',
    description15: 'Full-scale e-commerce analytics platform with real-time sales tracking, inventory management, customer insights, and business intelligence',
    description10: 'Simple dashboard showing basic sales numbers',
    complexity: 'Intermediate',
    route: '/ecommerce-dashboard',
    prompt: 'Build an e-commerce dashboard with analytics',
    features15: ['Real-time sales tracking', 'Advanced analytics', 'Inventory management', 'Customer segmentation', 'Predictive insights', 'Custom reporting'],
    features10: ['Basic charts', 'Simple metrics', 'Static data'],
    timeTaken15: '11 minutes',
    timeTaken10: '3.8 minutes',
    developerTime15: '16-24 weeks',
    developerTime10: '2-3 weeks'
  },
  {
    id: 'social-platform',
    title: 'Social Platform',
    description15: 'Complete social media platform with real-time messaging, content moderation, advanced feeds, stories, and social commerce',
    description10: 'Basic feed showing simple posts with like/comment',
    complexity: 'Advanced',
    route: '/social-platform',
    prompt: 'Create a social media application',
    features15: ['Real-time messaging', 'Advanced feed algorithms', 'Stories & media sharing', 'Content moderation AI', 'Social commerce', 'Live streaming'],
    features10: ['Basic posts', 'Like/comment', 'Simple feed'],
    timeTaken15: '10.5 minutes',
    timeTaken10: '4.5 minutes',
    developerTime15: '20-28 weeks',
    developerTime10: '3-4 weeks'
  },
  {
    id: 'project-manager',
    title: 'Project Suite',
    description15: 'Complete project management ecosystem with Gantt charts, resource planning, time tracking, reporting, and team collaboration',
    description10: 'Basic task list with assignment functionality',
    complexity: 'Expert',
    route: '/project-manager',
    prompt: 'Create a project management application',
    features15: ['Interactive Gantt charts', 'Resource allocation', 'Time tracking', 'Advanced reporting', 'Team collaboration', 'Budget management'],
    features10: ['Simple task lists', 'Basic assignment', 'Due dates'],
    timeTaken15: '12 minutes',
    timeTaken10: '5 minutes',
    developerTime15: '24-36 weeks',
    developerTime10: '4-6 weeks'
  }
];

const HomePage: React.FC = () => {
  const [selectedComplexity, setSelectedComplexity] = useState<string>('all');
  const [activeComparison, setActiveComparison] = useState<string | null>(null);
  const [animationState, setAnimationState] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationState(prev => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const filteredExamples = examples.filter(example => {
    return selectedComplexity === 'all' || example.complexity === selectedComplexity;
  });

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case 'Basic': return '#10B981';
      case 'Intermediate': return '#F59E0B';
      case 'Advanced': return '#EF4444';
      case 'Expert': return '#8B5CF6';
      default: return '#6B7280';
    }
  };

  const totalFeatures15 = examples.reduce((sum, ex) => sum + ex.features15.length, 0);
  const totalFeatures10 = examples.reduce((sum, ex) => sum + ex.features10.length, 0);
  const avgTime15 = Math.round(examples.reduce((sum, ex) => sum + parseInt(ex.timeTaken15), 0) / examples.length);
  const avgTime10 = Math.round(examples.reduce((sum, ex) => sum + parseInt(ex.timeTaken10), 0) / examples.length);

  return (
    <div className="homepage">
      <div className="hero-section">
        <div className="hero-background">
          <div className="floating-elements">
            <div className="element element-1"></div>
            <div className="element element-2"></div>
            <div className="element element-3"></div>
          </div>
        </div>
        
        <div className="hero-content">
          <div className="hero-badge">🚀 The Future of AI Development</div>
          <h1 className="hero-title">
            Synoppy <span className="gradient-text">Evolution</span>
            <br />Showcase
          </h1>
          <p className="hero-subtitle">
            Experience the revolutionary leap from basic AI assistance to production-ready, 
            scalable applications - all from the same simple prompts
          </p>
          
          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-number">{totalFeatures15}</span>
              <span className="stat-label">Advanced Features</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">8</span>
              <span className="stat-label">App Categories</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">2</span>
              <span className="stat-label">AI Versions</span>
            </div>
          </div>
        </div>
      </div>

      <div className="comparison-section">
        <div className="section-header">
          <h2>The Power Difference</h2>
          <p>Same prompts, dramatically different results</p>
        </div>
        
        <div className="comparison-container">
          <div className="version-comparison-modern">
            <div className="version-side synoppy-10-side">
              <div className="version-header">
                <div className="version-icon">📝</div>
                <h3>Synoppy 1.0</h3>
                <span className="version-subtitle">Basic Implementation</span>
              </div>
              <div className="version-stats">
                <div className="stat">
                  <span className="number">{totalFeatures10}</span>
                  <span className="label">Total Features</span>
                </div>
                <div className="stat">
                  <span className="number">{avgTime10}s</span>
                  <span className="label">Avg Build Time</span>
                </div>
                <div className="stat">
                  <span className="number">60%</span>
                  <span className="label">Feature Coverage</span>
                </div>
              </div>
              <div className="version-features">
                <div className="feature-item basic">✓ Basic functionality</div>
                <div className="feature-item basic">✓ Simple UI components</div>
                <div className="feature-item basic">✓ Local data storage</div>
                <div className="feature-item missing">✗ No real-time features</div>
                <div className="feature-item missing">✗ Limited scalability</div>
                <div className="feature-item missing">✗ Basic error handling</div>
              </div>
            </div>

            <div className="vs-divider-modern">
              <div className="vs-circle">VS</div>
              <div className="comparison-arrow"></div>
            </div>

            <div className="version-side synoppy-15-side">
              <div className="version-header">
                <div className="version-icon">🚀</div>
                <h3>Synoppy 1.5</h3>
                <span className="version-subtitle">Production Ready</span>
              </div>
              <div className="version-stats">
                <div className="stat">
                  <span className="number">{totalFeatures15}</span>
                  <span className="label">Total Features</span>
                </div>
                <div className="stat">
                  <span className="number">{avgTime15}s</span>
                  <span className="label">Avg Build Time</span>
                </div>
                <div className="stat">
                  <span className="number">95%</span>
                  <span className="label">Feature Coverage</span>
                </div>
              </div>
              <div className="version-features">
                <div className="feature-item advanced">✓ Enterprise-grade architecture</div>
                <div className="feature-item advanced">✓ Real-time capabilities</div>
                <div className="feature-item advanced">✓ Advanced UI/UX design</div>
                <div className="feature-item advanced">✓ Comprehensive testing</div>
                <div className="feature-item advanced">✓ Scalable infrastructure</div>
                <div className="feature-item advanced">✓ Production optimizations</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="examples-section">
        <div className="section-header">
          <h2>Interactive Examples</h2>
          <p>Click on any app to see both versions side by side</p>
          
          <div className="complexity-filter">
            <button 
              className={selectedComplexity === 'all' ? 'active' : ''}
              onClick={() => setSelectedComplexity('all')}
            >
              All Apps
            </button>
            {['Basic', 'Intermediate', 'Advanced', 'Expert'].map(level => (
              <button
                key={level}
                className={selectedComplexity === level ? 'active' : ''}
                onClick={() => setSelectedComplexity(level)}
              >
                {level}
              </button>
            ))}
          </div>
        </div>

        <div className="examples-grid-modern">
          {filteredExamples.map((example) => (
            <div key={example.id} className="example-comparison-card">
              <div className="card-header-modern">
                <h3>{example.title}</h3>
                <span 
                  className="complexity-badge-modern"
                  style={{ backgroundColor: getComplexityColor(example.complexity) }}
                >
                  {example.complexity}
                </span>
              </div>
              
              <div className="prompt-display">
                <strong>Prompt:</strong> "{example.prompt}"
              </div>

              <div className="app-versions">
                <Link to={`${example.route}?version=1.0`} className="version-preview v10-preview">
                  <div className="preview-header">
                    <span className="version-badge v10">v1.0</span>
                    <span className="build-time">{example.timeTaken10}</span>
                  </div>
                  <div className="preview-description">{example.description10}</div>
                  <div className="time-comparison">
                    <div className="time-item">
                      <span className="time-label">Synoppy:</span>
                      <span className="time-value synoppy">{example.timeTaken10}</span>
                    </div>
                    <div className="time-item">
                      <span className="time-label">Developer:</span>
                      <span className="time-value developer">{example.developerTime10}</span>
                    </div>
                  </div>
                  <div className="feature-count">{example.features10.length} features</div>
                </Link>

                <Link to={`${example.route}?version=1.5`} className="version-preview v15-preview">
                  <div className="preview-header">
                    <span className="version-badge v15">v1.5</span>
                    <span className="build-time">{example.timeTaken15}</span>
                  </div>
                  <div className="preview-description">{example.description15}</div>
                  <div className="time-comparison">
                    <div className="time-item">
                      <span className="time-label">Synoppy:</span>
                      <span className="time-value synoppy">{example.timeTaken15}</span>
                    </div>
                    <div className="time-item">
                      <span className="time-label">Developer:</span>
                      <span className="time-value developer">{example.developerTime15}</span>
                    </div>
                  </div>
                  <div className="feature-count">{example.features15.length} features</div>
                </Link>
              </div>

              <div className="detailed-comparison">
                <button 
                  className="compare-button"
                  onClick={() => setActiveComparison(activeComparison === example.id ? null : example.id)}
                >
                  {activeComparison === example.id ? 'Hide Comparison' : 'Compare Features'}
                </button>
                
                {activeComparison === example.id && (
                  <div className="feature-comparison">
                    <div className="features-column">
                      <h4>Synoppy 1.0 Features</h4>
                      {example.features10.map((feature, i) => (
                        <div key={i} className="feature-item-small basic">{feature}</div>
                      ))}
                    </div>
                    <div className="features-column">
                      <h4>Synoppy 1.5 Features</h4>
                      {example.features15.map((feature, i) => (
                        <div key={i} className="feature-item-small advanced">{feature}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="insights-section">
        <div className="section-header">
          <h2>Why Synoppy 1.5?</h2>
          <p>The evolution that changes everything</p>
        </div>
        
        <div className="insights-grid">
          <div className="insight-card">
            <div className="insight-icon">⚡</div>
            <h3>Speed Meets Quality</h3>
            <p>Build production-ready applications in seconds, not hours. Synoppy 1.5 generates scalable, enterprise-grade code faster than you can describe your requirements.</p>
          </div>
          <div className="insight-card">
            <div className="insight-icon">🏗️</div>
            <h3>Production Architecture</h3>
            <p>Every generated application follows industry best practices with proper error handling, security measures, performance optimizations, and scalable design patterns.</p>
          </div>
          <div className="insight-card">
            <div className="insight-icon">🔄</div>
            <h3>Real-time Everything</h3>
            <p>Built-in real-time capabilities, live data synchronization, collaborative features, and responsive updates - all generated automatically from simple prompts.</p>
          </div>
          <div className="insight-card">
            <div className="insight-icon">📊</div>
            <h3>Business Intelligence</h3>
            <p>Advanced analytics, comprehensive reporting, data visualization, and business insights integrated into every application without additional configuration.</p>
          </div>
        </div>
      </div>

      <div className="cta-section">
        <div className="cta-content">
          <h2>Experience the Difference</h2>
          <p>Start with any example above and witness the transformation from basic to extraordinary</p>
          <div className="cta-buttons">
            <button className="cta-primary" onClick={() => {
              const element = document.querySelector('.examples-section') as HTMLElement;
              window.scrollTo({ top: element?.offsetTop || 0, behavior: 'smooth' });
            }}>
              Explore Examples
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;